import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Monitor, Server, AlertTriangle, CheckCircle, XCircle, Users, MousePointerClick, CalendarX, DollarSign } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
export function CSRvsSSRSlide() {
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  const [ssrWidth, setSsrWidth] = useState(0);
  const [csrWidth, setCsrWidth] = useState(0);
  const [ssrLoads, setSsrLoads] = useState(0);
  const [ssrDone, setSsrDone] = useState(false);
  const [csrDone, setCsrDone] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const valRef = useRef(0);
  const ssrCountRef = useRef(0);
  const pausedRef = useRef(false);
  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  const startAnimation = useCallback(() => {
    cleanup();
    valRef.current = 0;
    ssrCountRef.current = 0;
    pausedRef.current = false;
    setSsrWidth(0);
    setCsrWidth(0);
    setSsrLoads(0);
    setSsrDone(false);
    setCsrDone(false);
    intervalRef.current = setInterval(() => {
      if (pausedRef.current) return;
      valRef.current += 1;
      const val = valRef.current;
      // SSR cycles 3x within 0-100
      const ssrProg = val * 3 % 100;
      setSsrWidth(Math.min(ssrProg, 100));
      setSsrDone(ssrProg >= 95);
      // Check SSR completions
      if (val === 33 || val === 66 || val === 100) {
        ssrCountRef.current += 1;
        setSsrLoads(ssrCountRef.current);
      }
      // CSR fills linearly
      setCsrWidth(val);
      setCsrDone(val >= 95);
      // When CSR reaches 100, pause — SSR bar stays filled (blue)
      if (val >= 100) {
        pausedRef.current = true;
        // Keep SSR bar at 100% during pause
        setSsrWidth(100);
        setSsrDone(true);
        timeoutRef.current = setTimeout(() => {
          // Reset everything for next loop
          valRef.current = 0;
          ssrCountRef.current = 0;
          pausedRef.current = false;
          setSsrWidth(0);
          setCsrWidth(0);
          setSsrLoads(0);
          setSsrDone(false);
          setCsrDone(false);
        }, 1500);
      }
    }, 30);
  }, [cleanup]);
  useEffect(() => {
    if (isVisible) {
      startAnimation();
    }
    return cleanup;
  }, [isVisible, startAnimation, cleanup]);
  // More realistic funnel based on industry data:
  // Google: 53% of mobile users leave if page takes >3s (Think with Google)
  // Average landing page conversion: 2.35% (Unbounce), top 25% get 5.31%
  // Lead-to-appointment rate: 20-30% for service businesses
  const funnelSteps = [{
    label: '1,000 Monthly Visitors',
    value: '1,000',
    icon: Users,
    loss: null
  }, {
    label: 'After 3-5s Load Time',
    value: '530',
    icon: XCircle,
    loss: '47% leave before page loads (Google data)'
  }, {
    label: 'Engaged Visitors',
    value: '320',
    icon: MousePointerClick,
    loss: '40% bounce without interacting'
  }, {
    label: 'Leads (form fills / calls)',
    value: '16',
    icon: CalendarX,
    loss: '5% lead conversion rate'
  }, {
    label: 'Booked Appointments',
    value: '4',
    icon: DollarSign,
    loss: '25% of leads actually book'
  }];
  return <section id="technology" className="min-h-screen bg-dark-section py-24 px-4 flex flex-col justify-center" data-id="element-185">
      <div className="max-w-7xl mx-auto w-full" data-id="element-186">
        <div className="text-center mb-16" data-id="element-187">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-188">
            Why Your Website Is Slow
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-189">
            Client-Side vs Server-Side Rendering
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" ref={ref} data-id="element-190">
          {/* CSR Card */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={isVisible ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} className="bg-dark-card rounded-2xl p-8 relative overflow-hidden border-l-4 border-red-500" data-id="element-191">
            <div className="flex items-center mb-6" data-id="element-192">
              <div className="p-3 bg-red-500/10 rounded-lg mr-4" data-id="element-193">
                <Monitor className="text-red-500" size={32} data-id="element-194" />
              </div>
              <h3 className="text-2xl font-bold text-white" data-id="element-195">
                Client-Side Rendering (CSR)
              </h3>
            </div>
            <p className="text-text-muted mb-8 text-lg leading-relaxed" data-id="element-196">
              ALL DIY website builders —{' '}
              <span className="text-white font-semibold" data-id="element-197">
                GoDaddy, Wix, GoHighLevel, Squarespace
              </span>{' '}
              — use client-side rendering because it's built to be cheap, not
              built to drive higher rankings with lightning-fast performance.
            </p>
            <div className="space-y-6" data-id="element-198">
              <div data-id="element-199">
                <div className="flex justify-between mb-2 text-sm font-medium" data-id="element-200">
                  <span className="text-red-400" data-id="element-201">Load Time</span>
                  <span className="text-red-400" data-id="element-202">3-5 seconds</span>
                </div>
                <div className="h-4 bg-dark rounded-full overflow-hidden" data-id="element-203">
                  <motion.div className="h-full bg-red-500" initial={{
                  width: 0
                }} animate={isVisible ? {
                  width: '85%'
                } : {}} transition={{
                  duration: 2,
                  ease: 'easeOut'
                }} data-id="element-204" />
                </div>
              </div>
              <div className="flex items-center text-red-400" data-id="element-205">
                <XCircle size={20} className="mr-2" data-id="element-206" />
                <span className="font-medium" data-id="element-207">
                  Search engines struggle to crawl
                </span>
              </div>
            </div>
          </motion.div>

          {/* SSR Card */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={isVisible ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-dark-card rounded-2xl p-8 relative overflow-hidden border-l-4 border-primary" data-id="element-208">
            <div className="flex items-center mb-6" data-id="element-209">
              <div className="p-3 bg-primary/10 rounded-lg mr-4" data-id="element-210">
                <Server className="text-primary" size={32} data-id="element-211" />
              </div>
              <h3 className="text-2xl font-bold text-white" data-id="element-212">
                Server-Side Rendering (SSR)
              </h3>
            </div>
            <p className="text-text-muted mb-8 text-lg leading-relaxed" data-id="element-213">
              Pre-render on server. Fast. SEO-optimized. Google crawls complete
              HTML immediately.
            </p>
            <div className="space-y-6" data-id="element-214">
              <div data-id="element-215">
                <div className="flex justify-between mb-2 text-sm font-medium" data-id="element-216">
                  <span className="text-primary" data-id="element-217">Load Time</span>
                  <span className="text-primary" data-id="element-218">&lt; 1 second</span>
                </div>
                <div className="h-4 bg-dark rounded-full overflow-hidden" data-id="element-219">
                  <motion.div className="h-full bg-primary" initial={{
                  width: 0
                }} animate={isVisible ? {
                  width: '20%'
                } : {}} transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                  delay: 0.5
                }} data-id="element-220" />
                </div>
              </div>
              <div className="flex items-center text-green-500" data-id="element-221">
                <CheckCircle size={20} className="mr-2" data-id="element-222" />
                <span className="font-medium" data-id="element-223">
                  Perfect crawlability & Indexing
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Live Performance Comparison */}
        <div className="bg-dark-card rounded-2xl p-8 border border-white/5" data-id="element-224">
          <h4 className="text-xl font-bold mb-6 text-center" data-id="element-225">
            Live Performance Comparison
          </h4>

          <div className="space-y-6" data-id="element-226">
            {/* SSR Race Bar */}
            <div className="relative" data-id="element-227">
              <div className="flex justify-between text-sm mb-2" data-id="element-228">
                <span className="font-bold text-white" data-id="element-229">LetLois (SSR)</span>
                <span className="text-primary font-mono font-bold" data-id="element-230">
                  Loaded: {ssrLoads}x
                </span>
              </div>
              <div className="h-8 bg-dark rounded-full overflow-hidden relative border border-white/5" data-id="element-231">
                <div className="h-full bg-primary absolute left-0 top-0" style={{
                width: `${ssrWidth}%`,
                transition: 'none'
              }} data-id="element-232" />
                {ssrDone && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-xs font-bold flex items-center" data-id="element-233">
                    <CheckCircle size={12} className="mr-1" data-id="element-234" /> LOADED
                  </div>}
              </div>
            </div>

            {/* CSR Race Bar */}
            <div className="relative" data-id="element-235">
              <div className="flex justify-between text-sm mb-2" data-id="element-236">
                <span className="font-bold text-white" data-id="element-237">Competitors (CSR)</span>
                <span className="text-red-400 font-mono font-bold" data-id="element-238">
                  Loaded: 1x
                </span>
              </div>
              <div className="h-8 bg-dark rounded-full overflow-hidden relative border border-white/5" data-id="element-239">
                <div className="h-full bg-red-500 absolute left-0 top-0" style={{
                width: `${csrWidth}%`,
                transition: 'none'
              }} data-id="element-240" />
                {csrDone && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-xs font-bold flex items-center" data-id="element-241">
                    <CheckCircle size={12} className="mr-1" data-id="element-242" /> LOADED
                  </div>}
              </div>
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-text-muted" data-id="element-243">
            SSR loads <span className="text-primary font-bold" data-id="element-244">3x faster</span>{' '}
            than CSR on average.
          </div>
        </div>

        {/* Slow Website Funnel Drop-off */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="mt-16 bg-dark-card rounded-2xl p-8 border border-red-500/20" data-id="element-245">
          <h4 className="text-2xl font-bold mb-2 text-center" data-id="element-246">
            What a Slow Website <span className="text-red-500" data-id="element-247">Actually</span>{' '}
            Costs You
          </h4>
          <p className="text-text-muted text-center mb-10 text-sm" data-id="element-248">
            Per 1,000 visitors with a 3-5 second load time — based on Google &
            Unbounce data
          </p>

          <div className="max-w-2xl mx-auto space-y-0" data-id="element-249">
            {funnelSteps.map((step, i) => {
            const widthPercent = 100 - i * 16;
            const isLast = i === funnelSteps.length - 1;
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5 + i * 0.15
            }} data-id="element-250">
                  {step.loss && <div className="flex items-center justify-center mb-1" data-id="element-251">
                      <div className="text-red-400 text-xs font-bold flex items-center bg-red-500/10 px-3 py-1 rounded-full border border-red-500/20" data-id="element-252">
                        <XCircle size={12} className="mr-1" data-id="element-253" />
                        {step.loss}
                      </div>
                    </div>}
                  <div className="mx-auto flex items-center justify-between px-4 py-3 rounded-lg mb-1" style={{
                width: `${widthPercent}%`,
                background: i === 0 ? 'rgba(255,255,255,0.08)' : `rgba(239, 68, 68, ${0.08 + i * 0.07})`,
                borderLeft: i > 0 ? '3px solid rgba(239, 68, 68, 0.5)' : '3px solid rgba(255,255,255,0.2)',
                borderRight: i > 0 ? '3px solid rgba(239, 68, 68, 0.5)' : '3px solid rgba(255,255,255,0.2)'
              }} data-id="element-254">
                    <span className={`text-sm font-medium ${i === 0 ? 'text-white' : 'text-red-300'}`} data-id="element-255">
                      {step.label}
                    </span>
                    <span className={`text-lg font-bold ${i === 0 ? 'text-white' : isLast ? 'text-red-500' : 'text-red-400'}`} data-id="element-256">
                      {step.value}
                    </span>
                  </div>
                </motion.div>;
          })}
          </div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 1.5
        }} className="mt-8 text-center" data-id="element-257">
            <p className="text-red-400 font-bold text-lg" data-id="element-258">
              With a fast SSR site, that same 1,000 visitors could yield{' '}
              <span className="text-green-400" data-id="element-259">12+ booked appointments</span> —
              3x more.
            </p>
            <p className="text-text-dim text-xs mt-2" data-id="element-260">
              Source: Google "Think with Google" mobile speed study, Unbounce
              conversion benchmark report
            </p>
          </motion.div>
        </motion.div>

        {/* Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.8
      }} className="mt-12 flex items-center justify-center p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl text-yellow-500" data-id="element-261">
          <AlertTriangle className="mr-3" data-id="element-262" />
          <span className="font-bold" data-id="element-263">
            Fact: Every second slower = 7% fewer conversions
          </span>
        </motion.div>
      </div>
    </section>;
}